// shared/ has no slice, so no model/ to hold hooks — colocate the hook next to
// the component per spec §4.3 (docs/superpowers/specs/2026-07-30-separate-ui-logic-design.md).
import { useSetState } from '@/shared/lib/customHooks';
import { useCallback, useEffect } from 'react';

/**
 * Drives the shared drawer layout wrapper: open and loading state plus the scroll shadow on its title bar. Mirrors the open and isLoading props into local state through effects, closes by clearing open and calling onCancel after the 200ms animation, exposes a loading toggle memoised on isLoading, and flips isScroll whenever the body's scrollTop moves away from or back to zero.
 *
 * @param props - Drawer props supplying open, isLoading and the onCancel callback.
 * @returns Drawer state together with the close, loading-toggle and scroll handlers.
 */
export const useWithDrawerLayout = (props: any) => {
  const [state, setState] = useSetState({
    isScroll: false,
    open: false,
    isLoading: false,
  });

  /**
   * Closes the drawer and notifies the parent once the closing animation has run. Clears the open flag immediately and calls the onCancel prop after a 200ms timeout.
   *
   * @returns Nothing.
   */
  const handleClose = () => {
    setState({ open: false });
    setTimeout(() => {
      props.onCancel();
    }, 200);
  };

  /**
   * Flips the drawer's loading flag. Negates the current isLoading value through the state setter and is memoised against that value.
   *
   * @returns Nothing.
   */
  const toggleLoading = useCallback(() => {
    setState({ isLoading: !state.isLoading });
  }, [state.isLoading]);

  /**
   * Tracks whether the drawer body is scrolled away from its top so the title bar can show a shadow. Reads scrollTop off the scroll event target and flips the isScroll flag only when it disagrees with the current value.
   *
   * @param e - Scroll event whose target provides the current scrollTop.
   * @returns Nothing.
   */
  const handleScroll = (e: any) => {
    const { scrollTop } = e.target;
    if (scrollTop === 0 && state.isScroll) {
      setState({ isScroll: false });
    } else if (scrollTop !== 0 && !state.isScroll) {
      setState({ isScroll: true });
    }
  };

  useEffect(() => {
    setState({ open: props.open });
  }, [props.open]);

  useEffect(() => {
    setState({ isLoading: props.isLoading });
  }, [props.isLoading]);

  return { state, handleClose, toggleLoading, handleScroll };
};
